type TTurnstileRenderOptions = {
  sitekey: string
  execution: 'render' | 'execute'
  appearance: 'always' | 'execute' | 'interaction-only'
  theme: 'auto' | 'light' | 'dark'
  callback: (token: string) => void
  'error-callback': (errorCode?: string) => void
  'expired-callback': () => void
  'timeout-callback': () => void
  'before-interactive-callback': () => void
}

type TTurnstile = {
  render: (container: HTMLElement, options: TTurnstileRenderOptions) => string
  execute: (widgetId: string) => void
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TTurnstile
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
// The invisible check finishes in a few seconds; a visitor solving an
// interactive challenge needs longer (Cloudflare also times it out itself).
const TOKEN_TIMEOUT_MS = 15_000
const INTERACTIVE_TOKEN_TIMEOUT_MS = 120_000

// One widget per page, shared by every caller on the client.
let scriptPromise: Promise<TTurnstile | null> | null = null
let scriptFailed = false
let widgetId: string | null = null
let widgetContainer: HTMLElement | null = null
let pending: {
  resolve: (result: ICaptchaResult) => void
  timer: ReturnType<typeof setTimeout>
  startedAt: number
  interactive: boolean
} | null = null

// Sends how the request went to RUM (AP-11209). The SDK is the one the
// Datadog plugin loads; without it initialised the action is dropped.
const reportToRum = (result: ICaptchaResult) => {
  import('@datadog/browser-rum')
    .then(({ datadogRum }) =>
      datadogRum.addAction('turnstile_token', {
        outcome: result.outcome,
        error_code: result.errorCode ?? null,
        interactive: result.interactive,
        duration_ms: Math.round(result.durationMs),
      })
    )
    .catch(() => {})
}

const finish = (result: ICaptchaResult) => {
  reportToRum(result)

  return result
}

const settle = (
  token: string | null,
  outcome: TCaptchaOutcome,
  errorCode?: string
) => {
  if (!pending) return

  const { resolve, timer, startedAt, interactive } = pending
  clearTimeout(timer)
  pending = null
  resolve(
    finish({
      token,
      outcome,
      errorCode,
      interactive,
      durationMs: performance.now() - startedAt,
    })
  )
}

const timeOut = () =>
  settle(null, pending?.interactive ? 'interactive_timeout' : 'timeout')

// Cloudflare wants the visitor to interact: give them time, and bring the
// widget into view, since it renders below the button they just pressed.
const onInteractive = () => {
  if (pending) {
    pending.interactive = true
    clearTimeout(pending.timer)
    pending.timer = setTimeout(timeOut, INTERACTIVE_TOKEN_TIMEOUT_MS)
  }

  widgetContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const loadTurnstile = (): Promise<TTurnstile | null> => {
  if (window.turnstile) return Promise.resolve(window.turnstile)

  scriptPromise ??= new Promise((resolve) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.onload = () => {
      scriptFailed = false
      resolve(window.turnstile ?? null)
    }
    // A blocked script must not block the form: requests go without a token
    // and the Hub decides per endpoint.
    script.onerror = () => {
      scriptPromise = null
      scriptFailed = true
      resolve(null)
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

const unavailable = (outcome: TCaptchaOutcome): ICaptchaResult => ({
  token: null,
  outcome,
  interactive: false,
  durationMs: 0,
})

/**
 * Cloudflare Turnstile for the public forms (AP-11102). `mount` renders an
 * invisible widget that only shows when Cloudflare wants an interaction;
 * `getToken` runs a fresh challenge per request, since the Hub accepts each
 * token once. It always resolves: without a token the result says why
 * (AP-11209), and callers send it on with `captchaHeaders`.
 */
export const useTurnstile = () => {
  const siteKey = useRuntimeConfig().public.turnstile?.siteKey ?? ''

  const mount = async (container: HTMLElement) => {
    if (!import.meta.client || !siteKey || widgetId) return

    const turnstile = await loadTurnstile()
    // The component may have unmounted while the script loaded; an orphaned
    // widget would hold `widgetId` and block every later mount.
    if (!turnstile || widgetId || !container.isConnected) return

    widgetContainer = container
    widgetId = turnstile.render(container, {
      sitekey: siteKey,
      execution: 'execute',
      appearance: 'interaction-only',
      theme: 'light',
      callback: (token) => settle(token, 'token'),
      'error-callback': (errorCode) =>
        settle(null, 'error', errorCode ? String(errorCode) : undefined),
      'expired-callback': () => settle(null, 'expired'),
      'timeout-callback': () => settle(null, 'interactive_timeout'),
      'before-interactive-callback': onInteractive,
    })
  }

  const unmount = () => {
    settle(null, 'cancelled')

    if (widgetId) window.turnstile?.remove(widgetId)
    widgetId = null
    widgetContainer = null
  }

  const getToken = (): Promise<ICaptchaResult> => {
    if (!siteKey) return Promise.resolve(finish(unavailable('not_configured')))

    const turnstile = import.meta.client ? window.turnstile : undefined
    if (!turnstile || !widgetId) {
      return Promise.resolve(
        finish(unavailable(scriptFailed ? 'script_blocked' : 'not_mounted'))
      )
    }

    const id = widgetId
    settle(null, 'cancelled')

    return new Promise((resolve) => {
      pending = {
        resolve,
        timer: setTimeout(timeOut, TOKEN_TIMEOUT_MS),
        startedAt: performance.now(),
        interactive: false,
      }
      turnstile.reset(id)
      turnstile.execute(id)
    })
  }

  return { mount, unmount, getToken }
}
